import { Perfil } from '../../domain/entities/Perfil';
import { Rol } from '../../domain/value-objects/Rol';

/**
 * Fila cruda del perfil tal como la devuelve la base de datos.
 * Todos los campos son opcionales a propósito: la RPC ha ido cambiando de
 * forma con el tiempo y el mapeador debe tolerar ausencias sin romperse.
 */
export interface FilaPerfil {
  id?: string | null;
  user_id?: string | null;
  nombre?: string | null;
  email?: string | null;
  telefono?: string | null;
  rol?: string | null;
  cliente_id?: number | string | null;
  activo?: boolean | null;
  bloqueado?: boolean | null;
  debe_cambiar_clave?: boolean | null;
  puede_usar_actuadores?: boolean | null;
  puede_ver_ubicaciones?: boolean | null;
  clientes_acceso?: unknown;
  areas_acceso?: unknown;
}

function aNumero(valor: unknown): number | null {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

/** Normaliza una lista de identificadores que puede llegar como array o JSON. */
function aListaNumeros(valor: unknown): number[] {
  if (!valor) return [];

  const bruto = typeof valor === 'string' ? intentarParsear(valor) : valor;
  if (!Array.isArray(bruto)) return [];

  return bruto
    .map((item) => {
      // La lista puede venir como [1,2] o como [{cliente_id:1}, …].
      if (item !== null && typeof item === 'object') {
        const registro = item as Record<string, unknown>;
        return aNumero(registro.cliente_id ?? registro.area_id ?? registro.id);
      }
      return aNumero(item);
    })
    .filter((n): n is number => n !== null);
}

function intentarParsear(texto: string): unknown {
  try {
    return JSON.parse(texto);
  } catch {
    return null;
  }
}

/**
 * Convierte la fila de la base de datos en una entidad de dominio.
 *
 * Las banderas ausentes toman el valor más restrictivo salvo donde el
 * comportamiento histórico dice lo contrario: `activo` y `puede_usar_actuadores`
 * son `true` por defecto porque así funciona el sistema actual, mientras que
 * `bloqueado` y `debe_cambiar_clave` sólo son ciertos si vienen explícitos.
 */
export function aPerfil(fila: FilaPerfil, correoSesion?: string | null): Perfil {
  const id = fila.id ?? fila.user_id ?? '';

  return new Perfil({
    id: String(id),
    nombre: fila.nombre ?? null,
    email: fila.email ?? correoSesion ?? null,
    telefono: fila.telefono ?? null,
    rol: Rol.from(fila.rol),
    clienteId: aNumero(fila.cliente_id),
    activo: fila.activo ?? true,
    bloqueado: fila.bloqueado ?? false,
    debeCambiarClave: fila.debe_cambiar_clave ?? false,
    puedeUsarActuadores: fila.puede_usar_actuadores ?? true,
    puedeVerUbicaciones: fila.puede_ver_ubicaciones ?? false,
    clientesAcceso: aListaNumeros(fila.clientes_acceso),
    areasAcceso: aListaNumeros(fila.areas_acceso),
  });
}
